import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Plus, RefreshCw, Shield, ShieldCheck, QrCode, MessageSquare, PhoneCall,
  CalendarDays, ChevronRight, AlertCircle, CheckCircle2, XCircle, X, Eye, Edit3,
  CreditCard, Users
} from "lucide-react";
import AppShell from "../components/app/AppShell";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { useAuth } from "../context/AuthContext";
import { useInbox } from "../context/InboxContext";
import { usePricing } from "../context/PricingContext";
import { useI18n } from "../i18n";
import { userLocale, fmtDate, flagEmoji } from "../utils/locale";
import LegalReacceptanceBanner from "../components/legal/LegalReacceptanceBanner";
import { protectionTypes } from "../lib/designTokens";

/**
 * RC-WEB-4 §12/§13/§14 — customer overview.
 * Compact summary + protections + real activity. No "Sin apps" claim, no
 * duplicated "Privados" action, one unread number (InboxContext).
 */

const API = "/api";
const POLLING_INTERVAL = 15000;

const Dashboard = () => {
  const { priceLabelSemantic, planMonths } = usePricing();
  const { user } = useAuth();
  const { unread, conversations } = useInbox();
  const { t, lang } = useI18n();
  const navigate = useNavigate();

  const [protections, setProtections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [membership, setMembership] = useState(null);
  const [trusted, setTrusted] = useState(null);
  const [calls, setCalls] = useState([]);
  const [justActivated, setJustActivated] = useState(null);
  const [activationDismissed, setActivationDismissed] = useState(false);
  const pollingRef = useRef(null);
  const previousRef = useRef([]);

  const locale = userLocale(lang, user?.country_code);

  const fetchProtections = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/vehicles`);
      return res.data;
    } catch (_e) {
      return null;
    }
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      const data = await fetchProtections();
      if (alive && data) {
        setProtections(data);
        previousRef.current = data;
      }
      if (alive) setLoading(false);
    })();
    axios.get(`${API}/user/membership`).then((r) => alive && setMembership(r.data)).catch(() => {});
    axios.get(`${API}/trusted-contacts`)
      .then((r) => {
        if (!alive) return;
        const rows = Array.isArray(r.data?.contacts || r.data) ? (r.data?.contacts || r.data) : [];
        const accepted = rows.filter((c) => c.status === "accepted");
        setTrusted({
          invited: rows.filter((c) => c.status === "invited").length,
          accepted: accepted.length,
          // GATE 7 — "operativo" = aceptado Y con al menos un perfil asignado.
          assigned: accepted.filter((c) => (c.vehicle_ids || []).length > 0).length,
        });
      })
      .catch(() => alive && setTrusted({ invited: 0, accepted: 0, assigned: 0 }));
    axios.get(`${API}/inbox/calls`).then((r) => alive && setCalls(r.data?.calls || [])).catch(() => {});
    return () => { alive = false; };
  }, [fetchProtections]);

  useEffect(() => {
    const poll = async () => {
      const status = await axios.get(`${API}/user/status`).then((r) => r.data).catch(() => null);
      if (!status) return;
      const next = status.vehicles || [];
      for (const v of next) {
        const prev = previousRef.current.find((p) => p.id === v.id);
        if (prev && prev.qr_status !== "active" && v.qr_status === "active") {
          setJustActivated(v);
          toast.success(`Tu QR para "${v.name}" ya está activo`, {
            description: "Ya puedes descargar e imprimir tu código",
            duration: 8000,
          });
        }
      }
      setProtections((prev) => next.map((v) => ({
        ...(prev.find((p) => p.id === v.id) || {}),
        ...v,
      })));
      previousRef.current = next;
    };
    pollingRef.current = setInterval(poll, POLLING_INTERVAL);
    return () => pollingRef.current && clearInterval(pollingRef.current);
  }, []);

  const handleRefresh = async () => {
    const data = await fetchProtections();
    if (data) {
      setProtections(data);
      previousRef.current = data;
      toast.info(t("app.dash.refreshed"));
    }
  };

  const activeCount = protections.filter((p) => p.qr_status === "active").length;
  const cstate = membership?.commercial_state;
  const ctaLabel = t({
    NUNCA_ACTIVO: "owner.dash.cta_activate",
    PAGO_EN_REVISION: "owner.dash.cta_view_payment",
    EN_GRACIA: "owner.dash.cta_renew_now",
    VENCIDA: "owner.dash.cta_reactivate",
  }[cstate] || "owner.dash.renew");

  const expiry = membership?.expires_at ? fmtDate(membership.expires_at, locale) : "—";
  const missedCalls = calls.filter((c) => c.state === "missed").length;
  const recent = conversations.slice(0, 4);

  const statusBadge = (qrStatus, paymentStatus, id) => {
    if (justActivated?.id === id && qrStatus === "active") {
      return <Badge className="bg-emerald-600 text-white">{t("app.dash.status.active")}</Badge>;
    }
    if (qrStatus === "active") return <Badge className="border border-emerald-300 bg-emerald-50 text-emerald-800">{t("app.dash.status.active")}</Badge>;
    if (paymentStatus === "pending") return <Badge className="border border-amber-300 bg-amber-50 text-amber-900">{t("app.dash.status.pending_payment")}</Badge>;
    if (paymentStatus === "rejected") return <Badge className="border border-red-300 bg-red-50 text-red-800">{t("app.dash.status.rejected")}</Badge>;
    if (paymentStatus === "payment_review") return <Badge className="border border-amber-300 bg-amber-50 text-amber-900">{t("app.dash.status.review")}</Badge>;
    return <Badge className="border border-slate-300 bg-slate-100 text-slate-700">{t("app.dash.status.pending")}</Badge>;
  };

  const stat = (icon, value, label, to, testid) => {
    const Icon = icon;
    const inner = (
      <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 transition-colors hover:bg-slate-50">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#1B84FF]/10 text-[#1B84FF]">
          <Icon className="h-5 w-5" />
        </span>
        <span className="min-w-0">
          <span className="block truncate text-xl font-bold text-[#0B2545]">{value}</span>
          <span className="block truncate text-xs text-slate-600">{label}</span>
        </span>
      </div>
    );
    return to ? <Link to={to} data-testid={testid}>{inner}</Link> : <div data-testid={testid}>{inner}</div>;
  };

  return (
    <AppShell
      title={t("app.dash.greeting", { name: user?.full_name?.split(" ")[0] || "" })}
      subtitle={activeCount > 0
        ? (activeCount === 1 ? t("app.dash.active_1") : t("app.dash.active_n", { n: activeCount }))
        : t("app.dash.activate_first")}
      actions={
        <Button variant="outline" size="sm" onClick={handleRefresh}
                className="hidden border-slate-300 text-[#0B2545] sm:inline-flex"
                data-testid="dashboard-refresh-btn">
          <RefreshCw className="mr-2 h-4 w-4" /> {t("app.dash.refresh")}
        </Button>
      }
    >
      <LegalReacceptanceBanner />

      {protections.some((p) => p.notices_paused) && (
        <div className="mb-5 flex items-start gap-3 rounded-2xl border border-amber-300 bg-amber-50 p-4" data-testid="paused-vehicles-banner">
          <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-amber-700" />
          <div>
            <p className="text-sm font-semibold text-amber-900">
              {protections.filter((p) => p.notices_paused).length === 1
                ? "1 protección con avisos en pausa"
                : `${protections.filter((p) => p.notices_paused).length} protecciones con avisos en pausa`}
            </p>
            <p className="text-xs text-amber-900/80">
              No recibirás avisos de esos QR hasta reanudarlos desde la ficha de la protección.
            </p>
          </div>
        </div>
      )}

      {membership?.revival_needed && (
        <div className="mb-5 flex items-start gap-3 rounded-2xl border border-red-300 bg-red-50 p-4" data-testid="revival-banner">
          <XCircle className="mt-0.5 h-5 w-5 shrink-0 text-red-700" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-red-900">{t("owner.dash.revival_title")}</p>
            <p className="text-xs text-red-900/80">{t("owner.dash.revival_hint")}</p>
            <Link to="/settings?s=notificaciones">
              <Button size="sm" className="mt-2 bg-red-600 text-white hover:bg-red-700" data-testid="revival-banner-reactivate-btn">
                Revisar avisos
              </Button>
            </Link>
          </div>
        </div>
      )}

      {membership?.activation_notice_for &&
        !["expired", "disabled", "standby", "none", "pending_payment"].includes(membership.status) &&
        localStorage.getItem(`activation_banner_${membership.activation_notice_for}`) !== "1" &&
        !activationDismissed && (
        <div className="mb-5 flex items-start justify-between gap-3 rounded-2xl border border-emerald-300 bg-emerald-50 p-4" data-testid="activation-banner">
          <div className="flex items-start gap-3">
            <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-700" />
            <div>
              <p className="text-sm font-semibold text-emerald-900">{t("owner.dash.activated_title")}</p>
              <p className="text-xs text-emerald-900/80">{t("owner.dash.activated_text")}</p>
            </div>
          </div>
          <button
            onClick={() => {
              try { localStorage.setItem(`activation_banner_${membership.activation_notice_for}`, "1"); } catch (_) {}
              setActivationDismissed(true);
            }}
            className="text-emerald-900/70 hover:text-emerald-900"
            data-testid="activation-banner-dismiss"
            aria-label="Cerrar"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {membership?.expires_at && typeof membership.days_remaining === "number" && membership.days_remaining <= 30 && (
        <div className="mb-5 flex flex-col gap-3 rounded-2xl border border-amber-300 bg-amber-50 p-4 sm:flex-row sm:items-center sm:justify-between" data-testid="renewal-banner">
          <p className="text-sm font-medium text-amber-900">
            {membership.days_remaining > 0
              ? <>Tu membresía vence el <strong>{expiry}</strong> · quedan {membership.days_remaining} días.</>
              : <>Tu membresía venció el <strong>{expiry}</strong>.</>}
          </p>
          <Link to="/renovar">
            <Button className="bg-[#0B2545] font-semibold text-white hover:bg-[#123a6b]" data-testid="renewal-banner-btn">
              {ctaLabel}
            </Button>
          </Link>
        </div>
      )}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-testid="dashboard-stats">
        {stat(QrCode, activeCount, t("app.dash.stat_qr"), "/dashboard/vehicles", "stat-active-qr")}
        {stat(MessageSquare, unread, t("app.dash.stat_unread"), "/mensajes", "stat-unread")}
        {stat(PhoneCall, missedCalls, t("app.dash.stat_missed"), "/llamadas", "stat-missed-calls")}
        {stat(Users, trusted ? trusted.assigned : "—", t("app.dash.stat_trusted"), "/contactos-de-confianza", "stat-trusted")}
      </div>

      <div className="mt-5 grid gap-5 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)]">
        <section data-testid="protections-section">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="flex items-center gap-2 text-lg font-bold text-[#0B2545]">
              <Shield className="h-5 w-5 text-[#1B84FF]" /> {t("app.dash.my_protections")}
            </h2>
            <Link to="/new-protection">
              <Button size="sm" className="bg-[#1B84FF] text-white hover:bg-[#0F6FDB]" data-testid="new-protection-btn">
                <Plus className="mr-1.5 h-4 w-4" /> {t("app.dash.new")}
              </Button>
            </Link>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="h-7 w-7 animate-spin rounded-full border-2 border-[#1B84FF] border-t-transparent" />
            </div>
          ) : protections.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-8 text-center" data-testid="protections-empty">
              <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-slate-100">
                <Shield className="h-7 w-7 text-slate-600" />
              </div>
              <h3 className="mb-2 text-lg font-semibold text-[#0B2545]">{t("app.dash.empty_title")}</h3>
              <p className="mx-auto mb-5 max-w-sm text-sm text-slate-600">{t("app.dash.empty_text")}</p>
              <Link to="/new-protection">
                <Button className="bg-[#1B84FF] text-white hover:bg-[#0F6FDB]" data-testid="create-first-protection-btn">
                  <Plus className="mr-2 h-4 w-4" /> {t("app.dash.create_first")}
                </Button>
              </Link>
            </div>
          ) : (
            <ul className="space-y-3">
              {protections.map((p) => (
                <li key={p.id}>
                  <div
                    onClick={() => navigate(`/vehicle/${p.id}`)}
                    className="cursor-pointer rounded-2xl border border-slate-200 bg-white p-4 transition-colors hover:bg-slate-50"
                    data-testid={`protection-row-${p.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <span className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl text-2xl ${
                        p.qr_status === "active" ? "bg-emerald-50" : "bg-slate-100"
                      }`}>
                        {protectionTypes[p.protection_type || "vehicle"]?.emoji || "🚗"}
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <h3 className="truncate text-base font-semibold text-[#0B2545]">{p.name}</h3>
                          {statusBadge(p.qr_status, p.payment_status, p.id)}
                        </div>
                        <p className="truncate text-xs text-slate-600">
                          {protectionTypes[p.protection_type || "vehicle"]?.label || "Vehículo"}
                          {p.plate_number ? ` · ${p.plate_number}` : ""}
                        </p>
                      </div>
                      <div className="flex shrink-0 items-center gap-2" onClick={(e) => e.stopPropagation()}>
                        {p.qr_status === "active" ? (
                          <>
                            <Link to={`/vehicle/${p.id}?tab=sticker`} className="hidden sm:block">
                              <Button variant="outline" size="sm" className="border-slate-300 text-[#0B2545]" data-testid={`sticker-btn-${p.id}`}>
                                <Edit3 className="mr-1.5 h-4 w-4" /> {t("app.dash.sticker")}
                              </Button>
                            </Link>
                            <Link to={`/vehicle/${p.id}`}>
                              <Button size="sm" className="bg-[#1B84FF] text-white hover:bg-[#0F6FDB]" data-testid={`view-btn-${p.id}`}>
                                <Eye className="mr-1.5 h-4 w-4" /> {t("app.dash.view")}
                              </Button>
                            </Link>
                          </>
                        ) : p.payment_status === "pending" ? (
                          <Link to={`/checkout/${p.id}`}>
                            <Button size="sm" className="bg-amber-600 text-white hover:bg-amber-700" data-testid={`pay-btn-${p.id}`}>
                              {t("app.dash.pay")} {priceLabelSemantic(planMonths)}
                            </Button>
                          </Link>
                        ) : null}
                        <ChevronRight className="h-5 w-5 text-slate-500" />
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        <div className="space-y-5">
          <section className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="membership-card">
            <div className="mb-3 flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-[#1B84FF]" />
              <h2 className="text-base font-bold text-[#0B2545]">{t("app.dash.membership")}</h2>
              {user?.country_code && (
                <span className="ml-auto rounded-full border border-slate-300 px-2 py-0.5 text-[11px] text-slate-700" data-testid="membership-country-badge">
                  {flagEmoji(user.country_code)} {user.country_code}
                </span>
              )}
            </div>
            <p className="text-sm font-semibold text-[#0B2545]" data-testid="membership-plan">
              {cstate === "NUNCA_ACTIVO"
                ? t("app.dash.no_membership")
                : t("app.dash.plan_months", { n: membership?.plan_months === 12 ? 12 : 6 })}
            </p>
            <p className="mt-0.5 flex items-center gap-1.5 text-xs text-slate-600" data-testid="membership-expiry">
              <CalendarDays className="h-3.5 w-3.5" />
              {cstate === "PAGO_EN_REVISION"
                ? t("app.dash.payment_review")
                : membership?.expires_at
                ? t("app.dash.expires_on", { date: expiry })
                : t("app.dash.activate_qr")}
            </p>
            <Link to="/renovar">
              <Button className="mt-4 w-full bg-[#0B2545] text-white hover:bg-[#123a6b]" data-testid="membership-renew-btn" data-state={cstate}>
                <CreditCard className="mr-2 h-4 w-4" /> {ctaLabel}
              </Button>
            </Link>
          </section>

          <section className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="recent-activity-card">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="text-base font-bold text-[#0B2545]">{t("app.dash.recent")}</h2>
              <Link to="/mensajes" className="text-xs font-semibold text-[#1B84FF] hover:underline" data-testid="activity-see-all">
                {t("app.dash.see_all")}
              </Link>
            </div>
            {recent.length === 0 ? (
              <p className="text-sm text-slate-600" data-testid="recent-activity-empty">
                {t("app.dash.recent_empty")}
              </p>
            ) : (
              <ul className="space-y-2">
                {recent.map((c) => (
                  <li key={c.id}>
                    <Link
                      to={`/mensajes?c=${c.id}`}
                      className="flex items-start gap-2 rounded-xl p-2 transition-colors hover:bg-slate-50"
                      data-testid={`activity-item-${c.id}`}
                    >
                      <span className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg ${
                        c.urgent ? "bg-red-100 text-red-700" : "bg-slate-100 text-slate-700"
                      }`}>
                        {c.is_call_request ? <PhoneCall className="h-4 w-4" /> : <MessageSquare className="h-4 w-4" />}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-medium text-[#0B2545]">
                          {c.profile_name || t("app.inbox.protection")}
                        </span>
                        <span className="block truncate text-xs text-slate-600">{c.last_message}</span>
                      </span>
                      {c.unread > 0 && (
                        <span className="rounded-full bg-red-600 px-1.5 text-[10px] font-bold text-white">{c.unread}</span>
                      )}
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* GATE 6/7 — estado REAL de los destinatarios de avisos, no copy de marketing. */}
          <section className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="trusted-readiness-card">
            <div className="mb-2 flex items-center justify-between gap-2">
              <h2 className="flex items-center gap-2 text-base font-bold text-[#0B2545]">
                <Users className="h-5 w-5 text-[#1B84FF]" /> {t("app.dash.trusted_ready")}
              </h2>
              <Link to="/contactos-de-confianza" className="text-xs font-semibold text-[#1B84FF] hover:underline"
                    data-testid="trusted-readiness-manage">
                {t("app.dash.trusted_manage")}
              </Link>
            </div>
            <p className="text-sm text-slate-700" data-testid="trusted-readiness-state">
              {!trusted
                ? "—"
                : trusted.assigned > 0
                ? t("app.dash.trusted_ok", { n: trusted.assigned })
                : trusted.accepted > 0
                ? t("app.dash.trusted_unassigned", { n: trusted.accepted })
                : trusted.invited > 0
                ? t("app.dash.trusted_pending", { n: trusted.invited })
                : t("app.dash.trusted_none")}
            </p>
          </section>
        </div>
      </div>
    </AppShell>
  );
};

export default Dashboard;
